// General
import React, { useState, useEffect, useContext } from "react";
// Styles
import { Modal } from "react-bootstrap";
import { TextField, Autocomplete } from "@mui/material";
import { Formik, Form, Field } from "formik";
// Redux & Context
import { useDispatch, useSelector } from "react-redux";
import { getCities, getDistricts } from "../../data/general/LocationSlice";
import { CustomerContext } from "../../data/customer/CustomerContext";
import CapitalizedFormikField from "../../components/CapitalizedFormikField";
import { useValidations } from "../../data/ValidationContext";

function CustomerAddModal({ show, handleClose }) {
  //  Context & Redux 
  const dispatch = useDispatch();
  const { addCustomer } = useContext(CustomerContext);
  const { vs_customer } = useValidations();
  const { cities, districts } = useSelector((state) => state.location);

  //  State 
  const [searchedCity, setSearchedCity] = useState("");

  //  Form Initial Values
  const initialValues = {
    fullName: "",
    phone1: "",
    phone2: "",
    customerType: "Bireysel",
    tcNo: "",
    vergiNo: "",
    vergiDairesi: "",
    city: "",
    district: "",
    address: "",
  };

  const fieldWrapperStyle = { marginBottom: "0.5rem" };

  //  Şehirleri Yükle 
  useEffect(() => {
    if (show && cities.length === 0) {
      dispatch(getCities({ signal: null }));
    }
  }, [show, cities.length, dispatch]);

  //  Render 
  return (
    <Modal show={show} onHide={handleClose} size="lg" centered style={{ zIndex: 9000 }}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={vs_customer}
        onSubmit={(values) => {
          addCustomer(values);
          handleClose();
        }}
      >
        {({ values, touched, errors, setFieldValue, setFieldTouched }) => (
          <Form>
            {/* Modal Header */}
            <Modal.Header closeButton>
              <Modal.Title>Müşteri Ekle</Modal.Title>
            </Modal.Header>

            {/* Modal Body */}
            <Modal.Body>
              {/*  Ad Soyad ve Müşteri Türü  */}
              <div className="row mb-3">
                <div className="col" style={fieldWrapperStyle}>
                  <CapitalizedFormikField
                    name="fullName"
                    label="Ad Soyad"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={touched.fullName && !!errors.fullName}
                    helperText={touched.fullName && errors.fullName}
                  />
                </div>
                <div className="col" style={fieldWrapperStyle}>
                  <select
                    name="customerType"
                    className="form-select"
                    value={values.customerType}
                    onChange={(e) => setFieldValue("customerType", e.target.value)}
                  >
                    <option value="Bireysel">Bireysel</option>
                    <option value="Kurumsal">Kurumsal</option>
                  </select>
                </div>
              </div>

              {/*  Bireysel / Kurumsal Alanlar  */}
              {values.customerType === "Bireysel" ? (


                <CapitalizedFormikField
                  name="tcNo"
                  label="T.C. Kimlik No"
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={touched.tcNo && !!errors.tcNo}
                  helperText={touched.tcNo && errors.tcNo}
                  className={"mb-3"}
                />
              ) : (
                <div className="row mb-3">
                  <div className="col" style={fieldWrapperStyle}>
                    <CapitalizedFormikField
                      name="vergiNo"
                      label="Vergi No"
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={touched.vergiNo && !!errors.vergiNo}
                      helperText={touched.vergiNo && errors.vergiNo}
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/\D/g, "");
                      }}
                    />
                  </div>
                  <div className="col" style={fieldWrapperStyle}>
                    <CapitalizedFormikField
                      name="vergiDairesi"
                      label="Vergi Dairesi"
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={touched.vergiDairesi && !!errors.vergiDairesi}
                      helperText={touched.vergiDairesi && errors.vergiDairesi}
                    />
                  </div>
                </div>
              )}

              {/*  Telefonlar  */}
              <div className="row mb-3">
                <div className="col" style={fieldWrapperStyle}>
                  <CapitalizedFormikField
                    name="phone1"
                    label="Telefon 1"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={touched.phone1 && !!errors.phone1}
                    helperText={touched.phone1 && errors.phone1}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, ""); // rakam dışı karakterleri sil
                    }}
                  />

                </div>
                <div className="col" style={fieldWrapperStyle}>

                  <CapitalizedFormikField
                    name="phone2"
                    label="Telefon 2"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={touched.phone2 && !!errors.phone2}
                    helperText={touched.phone2 && errors.phone2}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                  />
                </div>
              </div>

              {/*  Şehir ve İlçe  */}
              <div className="row g-3 mb-3">
                <div className="col-12 col-md-6">
                  <Autocomplete
                    options={cities}
                    value={values.city || null}
                    onChange={(e, newValue) => {
                      setFieldValue("city", newValue || "");
                      setFieldValue("district", "");
                      if (newValue && newValue !== searchedCity) {
                        dispatch(getDistricts({ signal: null, city: newValue }));
                        setSearchedCity(newValue);
                      }
                    }}
                    onBlur={() => setFieldTouched("city", true)}
                    getOptionLabel={(option) => option?.name || option || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Şehir"
                        size="small"
                        variant="outlined"
                        fullWidth
                        error={touched.city && !!errors.city}
                        helperText={touched.city && errors.city}
                      />
                    )}
                    slotProps={{
                      popper: {
                        modifiers: [
                          { name: "zIndex", enabled: true, phase: "write", fn: ({ state }) => { state.styles.popper.zIndex = 9000; } }
                        ],
                      },
                    }}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <Autocomplete
                    options={districts || []}
                    value={values.district || null}
                    onChange={(e, newValue) => setFieldValue("district", newValue || "")}
                    onBlur={() => setFieldTouched("district", true)}
                    getOptionLabel={(option) => option?.name || option || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="İlçe"
                        size="small"
                        variant="outlined"
                        fullWidth
                        error={touched.district && !!errors.district}
                        helperText={touched.district && errors.district}
                      />
                    )}
                    slotProps={{
                      popper: {
                        modifiers: [
                          { name: "zIndex", enabled: true, phase: "write", fn: ({ state }) => { state.styles.popper.zIndex = 9999; } }
                        ],
                      },
                    }}
                  />
                </div>
              </div>

              {/*  Adres  */}
              <div style={fieldWrapperStyle}>
                <CapitalizedFormikField
                  name="address"
                  label="Adres"
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                />
              </div>
            </Modal.Body>

            {/* Modal Footer */}
            <Modal.Footer>
              <button type="button" className="btn btn-secondary" onClick={handleClose}>
                İptal
              </button>
              <button type="submit" className="btn btn-primary">
                Kaydet
              </button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

export default CustomerAddModal;
